import { Box, Flex, Grid, Link, Spinner, Text } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Button from 'components/Button';
import { FormikCheckbox, FormikRadio, FormikSelect } from 'components/Formik';
import LogoLoader from 'components/Loader/LogoLoader';
import { useDisconnectQuickbookDesktop } from 'hooks/integrations/useDisconnectQuickbookDesktop';
import { useFetchPayrollOptions } from 'hooks/integrations/useFetchPayrollOptions';
import { useFetchQuickbookDesktopSettingList } from 'hooks/integrations/useFetchQuickbookDesktopSetting';
import { useGenerateQWCFile } from 'hooks/integrations/useGenerateQWCFile';
import {
  useSaveQuickbookDesktopSetting,
  IQuickBookDesktopSetting,
} from 'hooks/integrations/useSaveQuickbookDesktopSetting';
import { useCompanyInfo } from 'store/useUserStore';

import { initialQuickbookDesktopSettings } from './schema';

interface IQBDSetting {
  onDisconnect: () => void;
}

const QuickbookDesktopSetting: React.FC<IQBDSetting> = ({ onDisconnect }: IQBDSetting) => {
  const [formData, setFormData] = useState<any>(null);

  const { t } = useTranslation(['employees', 'common', 'integrations']);

  const navigate = useNavigate();
  const companyInfo = useCompanyInfo();

  const { mutateAsync: generateQWCAsync } = useGenerateQWCFile();
  const { status: qbdDisconnectStatus, mutateAsync: qbdDisconnectMutateAsync } = useDisconnectQuickbookDesktop();
  const { data: qbdSettingListData, status: qbdSettingListStatus, refetch } = useFetchQuickbookDesktopSettingList();
  const { status: qbdPayrollStatus, data: qbdPayrollData } = useFetchPayrollOptions();

  const { status: qbdSettingSaveStatus, mutateAsync: qbdSettingSaveMutateAsync } = useSaveQuickbookDesktopSetting();

  const quickbookWebConnectUrl =
    'https://developer.intuit.com/app/developer/qbdesktop/docs/get-started/get-started-with-quickbooks-web-connector';

  useEffect(
    () => {
      if (qbdSettingListStatus === 'success' && qbdPayrollStatus === 'success') {
        const settingData = qbdSettingListData?.payload[0];
        const pFormData = {
          is_billable: settingData?.is_billable === 1 ? ['1'] : [],
          shipping_address: settingData?.shipping_address === 1 ? '1' : '2',
          qb_double_overtime: settingData?.qb_double_overtime
            ? qbdPayrollData?.payload?.find((item: any) => item.value.toString() === settingData?.qb_double_overtime)
            : null,
          qb_overtime: settingData?.qb_overtime
            ? qbdPayrollData?.payload?.find((item: any) => item.value.toString() === settingData?.qb_overtime)
            : null,
          qb_regular: settingData?.qb_regular
            ? qbdPayrollData?.payload?.find((item: any) => item.value.toString() === settingData?.qb_regular)
            : null,
        };
        setFormData(pFormData);
      }
    },
    // eslint-disable-next-line
    [qbdSettingListStatus, qbdPayrollStatus]
  );

  const onDisconnectQBD = () => {
    qbdDisconnectMutateAsync().then(() => {
      onDisconnect();
    });
  };

  const onGenerateGWC = () => {
    generateQWCAsync().then(res => {
      const fileName = 'timeero_QBD.qwc';
      const gwcFile = new Blob([res], { type: 'text/plain' });
      const gwcFileUrl = URL.createObjectURL(gwcFile);
      const anchorTag = document.createElement('a');
      anchorTag.setAttribute('href', gwcFileUrl);
      anchorTag.setAttribute('download', fileName);
      anchorTag.dataset.downloadurl = ['text/plain', anchorTag.download, anchorTag.href].join(':');
      anchorTag.draggable = true;
      anchorTag.classList.add('dragout');
      anchorTag.click();
    });
  };

  const onConfigureMapping = () => {
    navigate('/integrations/quickbookDesktop/mapping');
  };

  const handleFormSubmit = async (values: typeof initialQuickbookDesktopSettings) => {
    const postData: IQuickBookDesktopSetting = {
      is_billable: values.is_billable.length ? 1 : 0,
      shipping_address: values.shipping_address,
      qb_double_overtime: values.qb_double_overtime?.value,
      qb_overtime: values.qb_overtime?.value,
      qb_regular: values.qb_regular?.value,
    };
    qbdSettingSaveMutateAsync(postData).then(() => {
      refetch();
    });
  };

  if ((qbdSettingListStatus === 'loading' && qbdPayrollStatus === 'loading') || !formData) return <LogoLoader />;

  return (
    <Box>
      <Grid templateColumns="repeat(2, 1fr)" gap={6}>
        <Flex flexDirection="column">
          <Box boxShadow="0 0 5px 0 #e1e1e1" borderRadius="5px" padding="10px 20px 20px 20px" mb={6}>
            <Text fontSize="md" mb={4}>
              {t('integrations:quickbooks.companyFile')}
            </Text>

            <Text fontSize="sm" color="gray.500" mb={4}>
              {companyInfo?.name}
            </Text>
            {/* <Text fontSize="sm" color="gray.500" mb={4}>
              QuickBooks Desktop Pro 2019 29.0
            </Text>*/}

            <Text mb={4} color="primary.300" cursor="pointer" onClick={onConfigureMapping}>
              {t('integrations:quickbooks.configureMappings')}
            </Text>

            <Text color="primary.300" onClick={onDisconnectQBD} cursor="pointer">
              {t('integrations:quickbooks.disconnect')} {qbdDisconnectStatus === 'loading' && <Spinner size="xs" />}
            </Text>
          </Box>
          <Box boxShadow="0 0 5px 0 #e1e1e1" borderRadius="5px" padding="10px 20px 20px 20px">
            <Text fontSize="md" mb={7}>
              {t('integrations:quickbooksDesktop.webConnectorSetup')}
            </Text>

            <Link
              mb={4}
              href={quickbookWebConnectUrl}
              cursor="pointer"
              color="primary.300"
              display="inline-block"
              isExternal
            >
              {t('integrations:quickbooksDesktop.downloadWebConnector')}
            </Link>

            <Text color="primary.300" cursor="pointer" onClick={onGenerateGWC}>
              {t('integrations:quickbooksDesktop.downloadQWCFile')}
            </Text>
          </Box>
        </Flex>
        <Box>
          <Box boxShadow="0 0 5px 0 #e1e1e1" borderRadius="5px" padding="10px 20px 20px 20px">
            <Text fontSize="md" mb={4}>
              {t('integrations:quickbooks.settings')}
            </Text>
            <Formik initialValues={formData} enableReinitialize onSubmit={handleFormSubmit}>
              {() => (
                <Form noValidate>
                  <Box display="flex">
                    <FormikCheckbox
                      options={[
                        { label: t('integrations:quickbooksDesktop.exportTimeActivitiesBillable'), value: '1' },
                      ]}
                      name="is_billable"
                    />
                  </Box>
                  <Box mb={6}>
                    <Text fontSize="sm" mb={2}>
                      {t('integrations:useBillingAddressAsJobLocation')}
                    </Text>
                    <FormikRadio
                      name="shipping_address"
                      options={[
                        { label: t('integrations:useShippingAddress'), value: '1' },
                        { label: t('integrations:useBillingAddress'), value: '2' },
                      ]}
                      stackProps={{ direction: 'column' }}
                      radioProps={{ mr: 3 }}
                      formControlProps={{ mb: 3 }}
                    />
                  </Box>
                  <Box>
                    <FormikSelect
                      name="qb_regular"
                      label={t('integrations:defaultRegularHoursPayrollItem')}
                      options={qbdPayrollData?.payload}
                      selectProps={{
                        placeholder: 'Select',
                        isClearable: true,
                      }}
                    />
                  </Box>
                  <Box>
                    <FormikSelect
                      name="qb_overtime"
                      label={t('integrations:defaultOvertimeHoursPayrollItem')}
                      options={qbdPayrollData?.payload}
                      selectProps={{
                        placeholder: 'Select',
                        isClearable: true,
                      }}
                    />
                  </Box>
                  <Box>
                    <FormikSelect
                      name="qb_double_overtime"
                      label={t('integrations:defaultDoubletimeHoursPayrollItem')}
                      options={qbdPayrollData?.payload}
                      selectProps={{
                        placeholder: 'Select',
                        isClearable: true,
                      }}
                    />
                  </Box>
                  <Button
                    type="submit"
                    colorScheme="secondary"
                    isLoading={qbdSettingSaveStatus === 'loading'}
                    minWidth="100px"
                  >
                    {t('common:save')}
                  </Button>
                </Form>
              )}
            </Formik>
          </Box>
        </Box>
      </Grid>
    </Box>
  );
};
export default QuickbookDesktopSetting;
